import { z } from 'zod';

import type { TFunction } from 'i18next';

type AdminT = TFunction<['common']>;

// Modele V1.2 §4.2 column constraints — keep these aligned with the Postgres
// CHECKs. Mismatches are caught by Vitest data-layer tests but the Zod layer
// gives the user a friendly message before the round-trip.
//
// title           : 10..120 chars (deals_title_length_check)
// description     : >= 50 chars (deals_description_min_length)
// area_sqft       : 100..50000 (deals_area_check)
// bedrooms        : 0..10 (deals_bedrooms_check)
// bathrooms       : 1..15 OR null (deals_bathrooms_check)
// asking_price    : > 0 AED, > 50000 to publish (deals_asking_price_positive
//                   + deals_check_status_transition publication precondition)
// type            : 'ready' | 'off_plan' (deals_type_check)
// status          : deal_status enum (FSM-controlled, see transition schema)

const TYPE_VALUES = ['ready', 'off_plan'] as const;
const STATUS_VALUES = [
  'draft',
  'validating',
  'published',
  'archived',
  'sold',
  'refused',
  'expired',
] as const;

export type AdminDealType = (typeof TYPE_VALUES)[number];
export type AdminDealStatus = (typeof STATUS_VALUES)[number];

function numberInRange(min: number, max: number, message: string) {
  return z.number({ message }).int({ message }).min(min, { message }).max(max, { message });
}

// Coerce empty strings ('') to null. RHF returns '' for empty optional
// text inputs, but the DB columns are nullable text — '' would write a
// valid-but-meaningless empty row. Input is `string | null`, output is
// `string | null` so the Zod resolver hands a stable shape back to RHF.
const nullableText = z.union([z.string(), z.null()]).transform((v) => {
  if (v === null) return null;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
});

export function adminDealTypeSchema(t: AdminT) {
  return z.object({
    type: z.enum(TYPE_VALUES, { message: t('admin.dealEdit.validation.typeInvalid') }),
    is_featured: z.boolean(),
    expiration_date: nullableText,
    internal_notes: nullableText,
    advisor_id: z.string().uuid().nullable().optional(),
  });
}
export type AdminDealTypeValues = z.infer<ReturnType<typeof adminDealTypeSchema>>;

// Empty / NaN coerced to null for nullable numeric DB columns. RHF passes
// NaN when the user clears a `valueAsNumber` input, so we match that
// surface here. Input is `number | null`, output is `number | null`.
const nullableInt = z.union([z.number(), z.null()]).transform((v) => {
  if (v === null) return null;
  if (typeof v !== 'number' || Number.isNaN(v)) return null;
  return Math.trunc(v);
});

const nullableNumber = z.union([z.number(), z.null()]).transform((v) => {
  if (v === null) return null;
  if (typeof v !== 'number' || Number.isNaN(v)) return null;
  return v;
});

export function adminDealPropertySchema(t: AdminT) {
  return z
    .object({
      title: z
        .string()
        .trim()
        .min(10, t('admin.dealEdit.validation.titleTooShort'))
        .max(120, t('admin.dealEdit.validation.titleTooLong')),
      description: z.string().trim().min(50, t('admin.dealEdit.validation.descriptionTooShort')),
      zone: z.string().trim().min(1, t('admin.dealEdit.validation.zoneRequired')),
      community: z.string().trim().min(1, t('admin.dealEdit.validation.communityRequired')),
      building: nullableText,
      // ADR-055 Lot 3 — canonical DLD keys persisted on combobox selection.
      // Hidden fields: NULL when the label is free text (the engine resolves
      // text/aliases at compute time); without these entries the Zod resolver
      // would STRIP the keys from the submitted values. Plain `string | null`
      // (no `.optional()`): RHF always supplies the defaults, and an optional
      // input would desync the resolver generics from the form values type.
      zone_key: nullableText,
      community_key: nullableText,
      building_key: nullableText,
      // M4 — Spec V1.1 §L624 (Google Maps embed approximatif). DB CHECK in
      // 20260522020000_deals_lat_lng_check.sql mirrors the bounds below and
      // adds the both-or-none clause enforced by the object-level refine.
      latitude: nullableNumber.refine(
        (v) => v === null || (v >= -90 && v <= 90),
        t('admin.dealEdit.validation.latitudeInvalid'),
      ),
      longitude: nullableNumber.refine(
        (v) => v === null || (v >= -180 && v <= 180),
        t('admin.dealEdit.validation.longitudeInvalid'),
      ),
      property_type: z.enum(
        ['apartment', 'villa', 'townhouse', 'penthouse', 'plot', 'commercial'],
        {
          message: t('admin.dealEdit.validation.propertyTypeInvalid'),
        },
      ),
      area_sqft: numberInRange(100, 50000, t('admin.dealEdit.validation.areaInvalid')),
      bedrooms: numberInRange(0, 10, t('admin.dealEdit.validation.bedroomsInvalid')),
      bathrooms: nullableInt.refine(
        (v) => v === null || (v >= 1 && v <= 15),
        t('admin.dealEdit.validation.bathroomsInvalid'),
      ),
      floor: nullableInt,
      view: nullableText.refine(
        (v) => v === null || v.length <= 50,
        t('admin.dealEdit.validation.viewTooLong'),
      ),
      handover: nullableText.refine(
        (v) => v === null || v.length <= 20,
        t('admin.dealEdit.validation.handoverTooLong'),
      ),
    })
    .refine((v) => (v.latitude === null) === (v.longitude === null), {
      message: t('admin.dealEdit.validation.coordsPartial'),
      path: ['longitude'],
    });
}
export type AdminDealPropertyValues = z.infer<ReturnType<typeof adminDealPropertySchema>>;

export function adminDealPriceSchema(t: AdminT) {
  return z
    .object({
      asking_price: z
        .number({ message: t('admin.dealEdit.validation.askingPriceInvalid') })
        .gt(0, t('admin.dealEdit.validation.askingPriceInvalid')),
      original_price: nullableNumber.refine(
        (v) => v === null || v > 0,
        t('admin.dealEdit.validation.originalPriceInvalid'),
      ),
      amount_paid: nullableNumber.refine(
        (v) => v === null || v > 0,
        t('admin.dealEdit.validation.amountPaidInvalid'),
      ),
    })
    .refine((v) => v.original_price === null || v.original_price >= v.asking_price, {
      message: t('admin.dealEdit.validation.askingExceedsOriginal'),
      path: ['asking_price'],
    })
    .refine(
      (v) =>
        v.amount_paid === null || v.original_price === null || v.amount_paid <= v.original_price,
      {
        message: t('admin.dealEdit.validation.amountPaidExceedsOriginal'),
        path: ['amount_paid'],
      },
    );
}
export type AdminDealPriceValues = z.infer<ReturnType<typeof adminDealPriceSchema>>;

export function adminDealSeoSchema(t: AdminT) {
  return z.object({
    meta_title: nullableText.refine(
      (v) => v === null || v.length <= 200,
      t('admin.dealEdit.validation.metaTitleTooLong'),
    ),
    meta_description: nullableText.refine(
      (v) => v === null || v.length <= 320,
      t('admin.dealEdit.validation.metaDescriptionTooLong'),
    ),
  });
}
export type AdminDealSeoValues = z.infer<ReturnType<typeof adminDealSeoSchema>>;

/**
 * Composite schema used by the Save action — concatenates every editable
 * field across the 4 visual tabs into a single submission shape. Use
 * {@link adminDealTransitionSchema} for status changes; this one excludes
 * `status` (FSM-controlled).
 */
export function adminDealEditSchema(t: AdminT) {
  return adminDealTypeSchema(t)
    .and(adminDealPropertySchema(t))
    .and(adminDealPriceSchema(t))
    .and(adminDealSeoSchema(t));
}
export type AdminDealEditValues = z.infer<ReturnType<typeof adminDealEditSchema>>;

/**
 * BO-2 create-mode schema. Same Type / Property / Price shape as the edit
 * schema, but **excludes** the SEO tab — meta_title / meta_description are
 * not collected at creation time (filled later via the BO-1 editor). The
 * 50-char minimum on `description` is preserved (D3) so the resulting draft
 * is publishable without first amending the description.
 */
export function adminDealCreateSchema(t: AdminT) {
  return adminDealTypeSchema(t).and(adminDealPropertySchema(t)).and(adminDealPriceSchema(t));
}
export type AdminDealCreateValues = z.infer<ReturnType<typeof adminDealCreateSchema>>;

export function adminDealTransitionSchema(t: AdminT) {
  return z
    .object({
      to_status: z.enum(STATUS_VALUES, {
        message: t('admin.dealEdit.validation.statusInvalid'),
      }),
      motif: nullableText,
    })
    .refine((v) => v.to_status !== 'refused' || (v.motif !== null && v.motif.length >= 20), {
      message: t('admin.dealEdit.validation.motifRequired'),
      path: ['motif'],
    });
}
export type AdminDealTransitionValues = z.infer<ReturnType<typeof adminDealTransitionSchema>>;
